import { getAiChatApiBase } from '../config';
import { chatbotConversationPath } from './paths';

function getAuthHeaders() {
  const token = window.localStorage.getItem('accessToken');
  return token ? { Authorization: token } : {};
}

/**
 * @param {string} block — one SSE event (lines separated by \n)
 * @returns {{ eventType: string, data: string }}
 */
function parseSseEventBlock(block) {
  let eventType = 'message';
  const dataLines = [];
  const lines = block.split(/\n/);
  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^\s/, ''));
    }
  }
  return { eventType, data: dataLines.join('\n') };
}

/**
 * @param {string} eventType
 * @param {string} data
 * @param {{
 *   acc: { assistantText: string, lastConversationId: string },
 *   onTextDelta?: (chunk: string) => void,
 *   onConversationId?: (id: string) => void,
 * }} sink
 */
function dispatchSseEvent(eventType, data, sink) {
  if (!data) {
    return;
  }
  if (eventType === 'message') {
    try {
      const obj = JSON.parse(data);
      if (obj && typeof obj.content === 'string') {
        sink.acc.assistantText += obj.content;
        if (obj.content.length > 0) {
          sink.onTextDelta?.(obj.content);
        }
      }
    } catch {
      /* ignore malformed chunk */
    }
  } else if (eventType === 'conversation') {
    try {
      const obj = JSON.parse(data);
      if (obj && typeof obj.conversationId === 'string') {
        sink.acc.lastConversationId = obj.conversationId;
        sink.onConversationId?.(obj.conversationId);
      }
    } catch {
      /* ignore */
    }
  }
}

/**
 * POST /chatbot — Server-Sent Events. Invokes onTextDelta for each content chunk (live UI).
 *
 * @param {{
 *   message: string,
 *   conversationId?: string,
 *   settings: object,
 *   onTextDelta?: (chunk: string) => void,
 *   onConversationId?: (id: string) => void,
 * }} params
 * @returns {Promise<{ assistantText: string, conversationId: string }>}
 */
export async function postChatbotSse({
  message,
  conversationId = '',
  settings,
  onTextDelta,
  onConversationId,
}) {
  const base = getAiChatApiBase();
  if (!base) {
    throw new Error('AI chat API base is not configured.');
  }
  const url = `${base}${chatbotConversationPath()}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      message,
      conversationId: conversationId ?? '',
      settings,
    }),
  });

  if (!res.ok) {
    let detail = '';
    try {
      detail = await res.text();
    } catch {
      /* ignore */
    }
    throw new Error(detail || `Chat request failed (${res.status})`);
  }

  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error('No response body from chat endpoint.');
  }

  const acc = { assistantText: '', lastConversationId: '' };
  const sink = { acc, onTextDelta, onConversationId };
  const decoder = new TextDecoder();
  let buffer = '';
  let streamDone = false;

  while (!streamDone) {
    // Sequential chunk reads are required for SSE parsing.
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    streamDone = done;
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    buffer = buffer.replace(/\r\n/g, '\n');

    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const rawBlock = buffer.slice(0, sep).trim();
      buffer = buffer.slice(sep + 2);
      if (!rawBlock || rawBlock.startsWith(':')) {
        continue;
      }
      const { eventType, data } = parseSseEventBlock(rawBlock);
      dispatchSseEvent(eventType, data, sink);
    }
  }

  const tail = buffer.trim();
  if (tail && !tail.startsWith(':')) {
    const { eventType, data } = parseSseEventBlock(tail);
    dispatchSseEvent(eventType, data, sink);
  }

  return {
    assistantText: acc.assistantText,
    conversationId: acc.lastConversationId,
  };
}
